import React, { useState } from "react"
import style from "./finder.mod.scss"
import { Button, Block, Input } from "@components"
import { useStaticQuery, navigate, graphql } from "gatsby"

/** Single-location showroom lists link to the location page; otherwise the list page. */
const resolveShowroomListLink = item => {
  const singleLocation = item.locations?.length === 1 && item.locations[0]
  return singleLocation ? singleLocation.path : item.path
}

const locationSidebarLabel = loc => {
  return loc.shortTitle || loc.businessName || loc.title || ""
}

const Finder = ({
  groupedLocationLists,
  locationLists,
  locations,
  children,
  allLocations,
  showVirtualConsultation = true
}) => {
  const [locationNotFound, setLocationNotFound] = useState(false)

  const checkPostcode = e => {
    const query = e.target[0].value
    const location = locations.findIndex(
      item =>
        item.areasServed &&
        item.areasServed
          .split(",")
          .map(area => area.trim())
          .includes(query)
    )
    if (location !== -1) navigate(locations[location].path)
    else {
      const prefix = query.slice(0, 2)
      const fallbackLocation = allLocations.findIndex(
        location =>
          location.postcodes && location.postcodes.split(",").includes(prefix)
      )
      if (fallbackLocation !== -1) navigate(allLocations[fallbackLocation].path)
      else setLocationNotFound(true)
    }
  }
  const data = useStaticQuery(graphql`
    query FinderQuery {
      content: contentYaml {
        showrooms {
          finder
        }
        virtualConsultation
        virtualTour
      }
    }
  `)

  return (
    <div className={style.finder}>
      <div className={style.finder__wrapper}>
        <div className={style.finder__top}>
          {showVirtualConsultation && (
            <>
              <h3>{"Can't make it into a showroom?"}</h3>
              <Block padding="bottom-half">
                <Button to={data.content.virtualConsultation} fullWidth>
                  Book a Virtual Consultation
                </Button>
              </Block>
              <Block padding="bottom">
                <Button to={data.content.virtualTour} fullWidth>
                  Showroom Virtual Tour
                </Button>
              </Block>
            </>
          )}
          <h3 className={style.finder__local}>{"Find your Local Showroom"}</h3>
          <p>{data.content.showrooms.finder}</p>
          <form
            className={style.finder__form}
            onSubmit={e => {
              e.preventDefault()
              checkPostcode(e)
            }}
          >
            <Input
              required
              label="Enter your Postcode"
              placeholder="Enter Postcode"
              pattern="\d{4}"
            />
            <Button type="submit" to={"/"}>
              Go
            </Button>
            {locationNotFound && (
              <p className={style.finder__error}>No location found</p>
            )}
          </form>
        </div>
        {(groupedLocationLists || locationLists) && (
          <div className={style.finder__bottom}>
            <h3>Our Locations</h3>
            {groupedLocationLists ? (
              <div className={style.finder__locationsGrouped}>
                {groupedLocationLists.map(region => {
                  const childRows = (() => {
                    if (region.children && region.children.length > 0) {
                      return region.children.map(child => {
                        const singleLoc =
                          child.locations?.length === 1 && child.locations[0]
                        const heading = singleLoc
                          ? locationSidebarLabel(singleLoc)
                          : child.heading
                        return {
                          key: child.id,
                          heading,
                          to: resolveShowroomListLink(child)
                        }
                      })
                    }
                    if (region.locations && region.locations.length > 0) {
                      return region.locations.map(loc => ({
                        key: loc.id,
                        heading: locationSidebarLabel(loc),
                        to: loc.path
                      }))
                    }
                    return []
                  })()

                  return (
                    <section
                      key={`region-${region.id}`}
                      className={style.finder__locationRegion}
                    >
                      <h4 className={style.finder__locationRegionTitle}>
                        <Button to={region.path} unstyled>
                          {region.heading}
                        </Button>
                      </h4>
                      {childRows.length > 0 && (
                        <ul className={style.finder__locationChildren}>
                          {childRows.map(row => (
                            <li key={`child-${row.key}`}>
                              <Button to={row.to} unstyled>
                                {row.heading}
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  )
                })}
              </div>
            ) : (
              <ul>
                {locationLists.map(item => {
                  const singleLocation =
                    item.locations.length === 1 && item.locations[0]

                  return (
                    <li key={`location-list-${item.id}`}>
                      <Button
                        to={singleLocation ? singleLocation.path : item.path}
                        unstyled
                      >
                        {item.heading}
                      </Button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}
      </div>
      {children && children}
    </div>
  )
}

export default Finder
